import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ExitIcon, GearIcon } from '@radix-ui/react-icons';
import { Avatar, IconButton } from '@mui/material';
import { apiClient } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import './AccountMenu.css';

const itemBase = 'group relative flex cursor-pointer select-none items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-800 dark:text-slate-200 outline-none hover:bg-slate-100 dark:hover:bg-slate-800 focus:bg-slate-100 dark:focus:bg-slate-800 transition-colors';

const AccountMenu: React.FC = () => {
  const navigate = useNavigate();
  const auth = React.useContext(AuthContext);
  const [initial, setInitial] = React.useState<string>('A');
  const [username, setUsername] = React.useState<string>('');
  const { isDark } = useThemeMode();

  React.useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const resp = await apiClient.get<{ username: string }>('/api/users/me');
        const uname = resp?.data?.username?.trim() ?? '';
        const ch = uname[0];
        if (!isMounted) return;
        if (uname) setUsername(uname);
        if (ch) setInitial(ch.toUpperCase());
      } catch {}
    })();
    return () => { isMounted = false; };
  }, []);

  if (!auth) {
    throw new Error('AccountMenu must be used within an AuthProvider');
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <IconButton aria-label="Menu du compte" size="small" className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
          <Avatar
            alt="Compte"
            sx={{
              width: 32,
              height: 32,
              fontSize: 14,
              fontWeight: 600,
              color: '#fff',
              background: 'linear-gradient(135deg,#3b82f6 0%,#4f46e5 100%)',
            }}
          >
            {initial}
          </Avatar>
        </IconButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="account-menu__content modern-dropdown z-50"
          sideOffset={10}
          align="end"
          collisionPadding={8}
          style={{ backgroundColor: isDark ? '#0f172a' : '#ffffff' }}
        >
          <div className="account-menu__header">
            <Avatar
              alt={username || 'Compte'}
              sx={{ width: 40, height: 40, fontSize: 16, fontWeight: 700, color: '#fff', background: 'linear-gradient(135deg,#3b82f6 0%,#4f46e5 100%)' }}
            >
              {initial}
            </Avatar>
            <div className="flex flex-col">
              <span className="account-menu__title">{username || 'Mon compte'}</span>
            </div>
          </div>
          <div className="account-menu__separator" />
          <DropdownMenu.Item className={`account-menu__item ${itemBase}`} onSelect={() => navigate('/account')}>
            <span className="account-menu__icon-badge">
              <GearIcon width={16} height={16} />
            </span>
            Mon compte
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="account-menu__separator" />
          <DropdownMenu.Item className={`account-menu__item account-menu__item--danger ${itemBase}`} onSelect={() => auth.logout()}>
            <span className="account-menu__icon-badge account-menu__icon-badge--danger">
              <ExitIcon width={16} height={16} />
            </span>
            Déconnexion
          </DropdownMenu.Item>
          <DropdownMenu.Arrow className="drop-shadow" style={{ fill: isDark ? '#0f172a' : '#ffffff', filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.15))' }} />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default AccountMenu; 